import { EInvoiceProfile, EInvoiceProfileJSON, PaginationInfo } from '@scrubtech/models'
import LoopError from '../store/errors/LoopError'
import { BaseApiClient } from './BaseApiClient'

export interface EInvoiceProfileCreateJSON {
  name: string
  tin: string
  identificationNumber: string
  identificationType: string
  sstNumber: string | null
  msicCode: string
  businessActivityDescription: string
  contactNumber: string
  emailAddress: string
  addressLine1: string
  addressLine2: string | null
  addressLine3: string | null
  postcode: string
  city: string
  state: string
  country: string
}

class EInvoiceProfilesApiClient extends BaseApiClient {
  constructor() {
    super('e-invoice-profiles')
  }

  async getProfiles(sorting: string = 'id', page: number = 1, limit: number = 30, search: string = ''): Promise<{ items: EInvoiceProfile[], paginationInfo: PaginationInfo }> {
    try {
      return await this.getList<EInvoiceProfile>(
        (json: EInvoiceProfileJSON) => new EInvoiceProfile(json),
        { sorting, page, limit, search }
      );
    } catch (err) {
      throw new LoopError(err, { sorting, page, limit, search });
    }
  }

  async getProfile(id: number): Promise<EInvoiceProfile> {
    try {
      return await this.getByID<EInvoiceProfile>(
        id,
        (json: EInvoiceProfileJSON) => new EInvoiceProfile(json)
      );
    } catch (err) {
      throw new LoopError(err, { id });
    }
  }

  async createProfile(data: Partial<EInvoiceProfileCreateJSON>): Promise<EInvoiceProfile> {
    try {
      console.log(data)
      return await this.create<EInvoiceProfile, Partial<EInvoiceProfileCreateJSON>>(
        data,
        (json: EInvoiceProfileJSON) => new EInvoiceProfile(json)
      );
    } catch (err) {
      if (err instanceof LoopError) {
        throw new LoopError(err.error, { ...err.requestParams, ...data });
      }
      throw new LoopError(err, data);
    }
  }

  async updateProfile(id: number, data: Partial<EInvoiceProfileCreateJSON>): Promise<EInvoiceProfile> {
    try {
      console.log(data)
      return await this.update<EInvoiceProfile, Partial<EInvoiceProfileCreateJSON>>(
        id,
        data,
        (json: EInvoiceProfileJSON) => new EInvoiceProfile(json)
      );
    } catch (err) {
      if (err instanceof LoopError) {
        throw new LoopError(err.error, { ...err.requestParams, ...data });
      }
      throw new LoopError(err, { id, ...data });
    }
  }

  async deleteProfile(id: number): Promise<EInvoiceProfile> {
    try {
      return await this.delete<EInvoiceProfile>(
        id,
        (json: EInvoiceProfileJSON) => new EInvoiceProfile(json)
      );
    } catch (err) {
      if (err instanceof LoopError) {
        throw new LoopError(err.error, { ...err.requestParams, id });
      }
      throw new LoopError(err, { id });
    }
  }
}

// Create a singleton instance
const eInvoiceProfilesApi = new EInvoiceProfilesApiClient();

// Export the API functions with the same interface as before
export const getEInvoiceProfiles = (sorting?: string, page?: number, limit?: number, search?: string) => 
  eInvoiceProfilesApi.getProfiles(sorting, page, limit, search);

export const getEInvoiceProfile = (id: number) => 
  eInvoiceProfilesApi.getProfile(id);

export const createEInvoiceProfile = (data: Partial<EInvoiceProfileCreateJSON>) => 
  eInvoiceProfilesApi.createProfile(data);

export const updateEInvoiceProfile = (id: number, data: Partial<EInvoiceProfileCreateJSON>) => 
  eInvoiceProfilesApi.updateProfile(id, data);

export const deleteEInvoiceProfile = (id: number) => 
  eInvoiceProfilesApi.deleteProfile(id); 
